import { Code2 } from 'lucide-react'
import type { BusinessLogicValidation } from '../txn/types'

// Shared "Business Logic" sub-form used by EVERY transaction-designer surface
// that configures a business-logic function — column item_change, row events,
// field/form validations, action buttons, cross-updates, follow-up actions and
// the pre-commit / transaction-complete hooks. It always offers the same four
// inputs (function name, description, language, functional specification) plus a
// working "Open in code editor" link, so the experience is identical everywhere.
//
// The functional specification is the detailed requirement that drives LLM /
// agent code generation and is written into the generated code file's header.

interface Props {
  value: BusinessLogicValidation
  onChange: (bl: BusinessLogicValidation) => void
  // When provided, an "Open in code editor" button is shown. The parent is
  // responsible for first committing the current value (so the stub file is
  // generated from the latest method/spec) and then navigating.
  onOpenCode?: () => void
  // Optional override label for the function-name field.
  methodLabel?: string
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '7px 10px', background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)', borderRadius: 6,
  color: 'var(--color-text)', fontSize: '0.9125rem', minHeight: 36, boxSizing: 'border-box',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
  fontWeight: 600,
  marginBottom: 4, marginTop: 12,
}
const helpStyle: React.CSSProperties = {
  fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, fontStyle: 'italic',
}

export default function BusinessLogicFields({ value, onChange, onOpenCode, methodLabel }: Props) {
  const patch = (p: Partial<BusinessLogicValidation>) => onChange({ ...value, ...p })
  return (
    <>
      <label style={labelStyle}>{methodLabel || 'Function name'}</label>
      <input
        style={inputStyle}
        value={value.method || ''}
        placeholder="e.g. validate_customer"
        onChange={(e) => patch({ method: e.target.value })}
      />

      <label style={labelStyle}>Description</label>
      <input
        style={inputStyle}
        value={value.description || ''}
        placeholder="Short label, e.g. Validate customer is active"
        onChange={(e) => patch({ description: e.target.value })}
      />

      <label style={labelStyle}>Language</label>
      <select
        style={{ ...inputStyle, cursor: 'pointer' }}
        value={value.language || ''}
        onChange={(e) => patch({ language: (e.target.value || undefined) as BusinessLogicValidation['language'] })}
      >
        <option value="">Project default</option>
        <option value="plpgsql">PL/pgSQL (.sql)</option>
        <option value="plsql">Oracle PL/SQL (.sql)</option>
        <option value="python">Python (.py)</option>
        <option value="java">Java (.java)</option>
      </select>
      <p style={helpStyle}>
        Language for THIS function. Leave as "Project default" to use the
        project's business-logic language. PL/pgSQL + Java require Postgres;
        Oracle PL/SQL requires an Oracle database.
      </p>

      <label style={labelStyle}>Functional specification</label>
      <textarea
        style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }}
        value={value.spec || ''}
        placeholder={
          'Describe in detail what this function must do — the inputs it reads, '
          + 'the rules it applies, and what it returns / sets. This drives AI code '
          + 'generation and is written into the code file header.'
        }
        onChange={(e) => patch({ spec: e.target.value })}
      />

      {onOpenCode && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={onOpenCode}
          style={{ marginTop: 10, paddingLeft: 0 }}
          title="Generate the stub (from the spec above) and open it in the code editor"
        >
          <Code2 size={14} /> Open in code editor
        </button>
      )}
    </>
  )
}
